<template>
    <Card :title="title">
        <template
            v-if="isFlashcard"
            #imageArea
        >
            <div :class="['min-w-fit flex items-center justify-center bg-yellow-50 rounded-lg p-1']">
                <i
                    :class="[
                        narrowMode || $attrs.useSmallImg ? 'w-8 h-8' : 'w-10 h-10',
                        'icon text-slate-600 icon-flashcards',
                    ]"
                ></i>
            </div>
        </template>
        <template
            v-if="context.type === 'document' && context.has_ai_content"
            #titleAddonArea
        >
            <TagAi size="sm" />
        </template>
        <template #contentArea>
            <div :class="['flex min-w-0 text-body-3', stackMode ? 'flex-col' : 'space-x-1']">
                <div
                    v-if="courseName || uniName"
                    class="text-slate-600 flex truncate items-center space-x-2"
                >
                    <component
                        :is="courseLink ? 'Link' : 'div'"
                        v-if="courseName"
                        :href="courseLink"
                        :class="['font-medium truncate', { 'text-blue-600': courseLink }]"
                        @navigate.stop
                        >{{ courseName }}</component
                    >
                    <component
                        :is="uniLink ? 'Link' : 'div'"
                        v-if="uniName"
                        :href="uniLink"
                        :class="['truncate', { 'text-blue-600': uniLink }]"
                        @navigate.stop
                        >{{ uniName }}
                    </component>
                </div>
                <div
                    v-if="userName || stackMode"
                    class="flex min-w-0 items-center space-x-1"
                >
                    <span
                        v-if="userName?.trim()"
                        class="flex min-w-0 font-body-3 text-slate-600"
                    >
                        <i18next :translation="$t('common:byAuthor')">
                            <template #author>
                                <component
                                    :is="userLink ? 'Link' : 'span'"
                                    :href="userLink"
                                    :class="['ml-1 font-medium truncate', { 'text-blue-600': userLink }]"
                                    @navigate.stop
                                >
                                    {{ userName }}
                                </component>
                                <TagKarma
                                    v-if="typeof userKarma === 'number'"
                                    :text="userKarma"
                                    size="sm"
                                    class="ml-1"
                                />
                            </template>
                        </i18next>
                    </span>
                    <span
                        v-if="stackMode"
                        class="font-caption-regular text-slate-400"
                        v-text="humanReadableTime"
                    ></span>
                </div>
            </div>
        </template>
        <template #primaryActionArea>
            <div class="flex items-center space-x-2">
                <div
                    v-if="!stackMode"
                    class="leading-4 text-slate-400 text-xs"
                    v-text="humanReadableTime"
                ></div>
                <TagDocumentType
                    v-if="isDocument"
                    :type="type"
                    size="sm"
                />
                <Tag
                    v-else-if="isFlashcard"
                    data-testid="flashcard-type"
                    color-class="text-orange-300 bg-orange-50"
                    :text="$t('flashcard_other')"
                    size="sm"
                />
            </div>
        </template>
        <template #secondaryActionArea>
            <TagList
                :tags="getStats"
                tag-bare
                tag-size="sm"
            />
        </template>
    </Card>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import { round } from 'lodash-es';
import Card, { CardMode } from '@/components/Card';
import TagDocumentType from '@/components/TagDocumentType';
import type { TagBase } from '@/components/TagList/TagList';
import type { DocumentType } from '@/components/TagDocumentType';
import dayjs from '@/plugins/dayjs';
import TagAi from '@/components/TagAi';
import TagKarma from '@/components/TagKarma';
import { getCourseLink, getUniLink, getUserLink } from '@/router/links';
import type { DocumentContext, FlashcardContext } from '@/components/CardSearchResult';
import { convertToReadableNumber } from '@/helpers/string';

/**
 * The CardSearchResult component is integrated into the search page to effectively present search outcomes across
 * various contexts, such as documents and flashcards. It seamlessly incorporates the Card and TagList components.
 * All attributes from its parent components are transmitted to the Card component. The card is designed
 * to be interactive; it can be made clickable by assigning onClick and clickable attributes to it.
 * Otherwise, the courses and universities within it serve as clickable elements.
 */
export default defineComponent({
    name: 'CardSearchResult',
    components: {
        Card,
        TagDocumentType,
        TagKarma,
        TagAi,
    },
    props: {
        /**
         * Main title of card
         */
        title: {
            type: String,
            required: true,
        },
        /**
         * Screen size value to adjust the card style
         */
        enableLinks: {
            type: Boolean,
            default: false,
        },
        /**
         * University name
         */
        uniName: {
            type: String,
            default: undefined,
        },
        /**
         * University id
         */
        uniId: {
            type: Number,
            default: undefined,
        },
        /**
         * course name
         */
        courseName: {
            type: String,
            default: undefined,
        },
        /**
         * course id
         */
        courseId: {
            type: Number,
            default: undefined,
        },
        /**
         * user karma
         */
        userKarma: {
            type: Number,
            default: undefined,
        },
        /**
         * user name
         */
        userName: {
            type: String,
            default: undefined,
        },
        /**
         * user id
         */
        userId: {
            type: Number,
            default: undefined,
        },
        /**
         * Type of context
         */
        type: {
            type: [String, Number] as PropType<DocumentType | number>,
            required: true,
        },
        /**
         * context object, to render Stats for each context properly Document type has to have
         * downloads up_votes and posts attributes and Fl type has to have rating, learners attributes.
         */
        context: {
            type: Object as PropType<FlashcardContext | DocumentContext>,
            required: true,
        },
        /**
         * Human readable creation time
         */
        createdAt: {
            type: Date,
            default: undefined,
        },
        /**
         * Custom stats of context
         */
        stats: {
            type: Array<TagBase>,
            default: [],
        },
    },
    computed: {
        courseLink() {
            return (
                (this.enableLinks &&
                    this.courseName &&
                    this.courseId &&
                    getCourseLink(this.courseId, this.courseName)) ||
                undefined
            );
        },
        uniLink() {
            return (
                (this.enableLinks && this.uniName && this.uniId && getUniLink(this.uniId, this.uniName)) || undefined
            );
        },
        userLink() {
            return (
                (this.enableLinks && this.userName && this.userId && getUserLink(this.userId, this.userName)) ||
                undefined
            );
        },
        stackMode(): boolean {
            return this.$attrs.mode === CardMode.Stack;
        },
        narrowMode(): boolean {
            return this.$attrs.mode === CardMode.Narrow;
        },
        humanReadableTime() {
            // @ts-ignore cannot find fromNow method
            return dayjs(this.createdAt).fromNow(true);
        },
        isFlashcard(): boolean {
            return this.context.type === 'flashcard';
        },
        isDocument(): boolean {
            return this.context.type === 'document';
        },
        getStats(): TagBase[] {
            const rating = `
                <span>${round(Number(this.context.avg_star_score), 1)}
                    <span class="text-slate-400">(${this.convertToReadableNumber(this.context.ratings_count)})</span>
                </span>
            `;

            if (this.context.type === 'document') {
                return [
                    {
                        value: rating,
                        icon: this.context.user_rated ? 'star-full' : 'star',
                        iconClass: this.context.user_rated ? 'text-yellow-300' : undefined,
                    },
                    { value: this.context.questions, icon: 'discussions' },
                    { value: this.context.downloads, icon: 'downloads' },
                ];
            }
            if (this.context.type === 'flashcard') {
                return [
                    {
                        value: rating,
                        icon: this.context.user_rated ? 'star-full' : 'star',
                        iconClass: this.context.user_rated ? 'text-yellow-300' : undefined,
                    },
                    { value: this.context.learners, icon: 'play' },
                ];
            }
            return this.stats;
        },
    },
    methods: {
        convertToReadableNumber,
    },
});
</script>

<style scoped lang="scss">
:deep(.content-area) {
    @apply w-full min-w-0;
}
</style>
